import type { UserRole } from "./roles.js";

export const PERMISSIONS = [
  "company:settings",
  "company:billing:read",
  "company:billing:write",
  "company:privacy",
  "company:onchain",
  "users:manage",
  "sites:read",
  "sites:write",
  "workers:read",
  "workers:write",
  "equipment:read",
  "equipment:write",
  "pay_rules:read",
  "pay_rules:write",
  "pay_periods:read",
  "pay_periods:approve",
  "pay_periods:lock",
  "time_entries:read",
  "time_entries:write",
  "time_entries:submit_day",
  "exports:read",
  "exports:full_payroll",
  "audit:read",
  "reports:read",
  "payroll:sensitive",
  "geofence:configure",
  "geofence:read",
  "worker_documents:read",
  "worker_documents:read_all",
  "worker_documents:write",
] as const;

export const PLATFORM_PERMISSIONS = ["platform:beta_access:manage"] as const;
export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[number];

export type Permission = (typeof PERMISSIONS)[number];

const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  OWNER: PERMISSIONS,
  ADMIN: [
    "company:settings",
    "company:billing:read",
    "company:privacy",
    "users:manage",
    "sites:read",
    "sites:write",
    "workers:read",
    "workers:write",
    "equipment:read",
    "equipment:write",
    "pay_rules:read",
    "pay_rules:write",
    "pay_periods:read",
    "pay_periods:approve",
    "pay_periods:lock",
    "time_entries:read",
    "time_entries:write",
    "time_entries:submit_day",
    "exports:read",
    "exports:full_payroll",
    "audit:read",
    "reports:read",
    "payroll:sensitive",
    "geofence:configure",
    "geofence:read",
    "worker_documents:read_all",
    "worker_documents:write",
  ],
  GM_PAYROLL: [
    "pay_periods:read",
    "pay_periods:approve",
    "pay_periods:lock",
    "pay_rules:read",
    "pay_rules:write",
    "reports:read",
    "exports:read",
    "exports:full_payroll",
    "audit:read",
    "sites:read",
    "sites:write",
    "workers:read",
    "workers:write",
    "equipment:read",
    "equipment:write",
    "time_entries:read",
    "time_entries:write",
    "payroll:sensitive",
    "geofence:configure",
    "geofence:read",
    "worker_documents:read_all",
    "worker_documents:write",
  ],
  SITE_MANAGER: [
    "sites:read",
    "workers:read",
    "equipment:read",
    "time_entries:read",
    "time_entries:write",
    "time_entries:submit_day",
    "reports:read",
    "audit:read",
    "geofence:read",
    "worker_documents:read",
  ],
  // Sensitive documents require an explicit future grant; auditors have no default document access.
  AUDITOR: ["reports:read", "audit:read", "exports:read", "sites:read", "workers:read", "equipment:read", "geofence:read"],
  EMPLOYEE: ["time_entries:read"],
};

export function roleHasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function roleHasAllSitesAccess(role: UserRole): boolean {
  return role !== "SITE_MANAGER" && role !== "EMPLOYEE";
}
